import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'auth/AuthProvider';
import { ApiError } from 'core/errors/ApiError';
import { invalidateAfterAssessmentStart } from 'core/api/invalidation';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import {
  mapAccessCodeInspectPreview,
  normalizeAccessCode,
  resolveRedeemAttemptId,
} from 'features/assessment/accessCodeUtils';
import { mapAccessCodeError } from 'features/assessment/mapAccessCodeError';
import { companyApi } from 'features/company/company.api';
import { Button } from 'shared/components/Button';
import { Card, StatusChip } from 'shared/components/Feedback';
import { TextInput } from 'shared/components/Input';
import { LiveRegion } from 'shared/components/ResultPresentation';
import { formatDateTime } from 'shared/utilities/format';

export function CompanyCodeSection({ authReturnTo = paths.assessmentIntro }) {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { status } = useAuth();
  const codeId = useId();
  const consentId = useId();
  const previewRef = useRef(null);
  const [code, setCode] = useState('');
  const [consent, setConsent] = useState(false);
  const [step, setStep] = useState('enter');
  const [preview, setPreview] = useState(null);
  const [validating, setValidating] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  useEffect(() => {
    if (step === 'consent' && previewRef.current) {
      previewRef.current.focus();
    }
  }, [step]);

  function resetFlow() {
    setCode('');
    setConsent(false);
    setPreview(null);
    setStep('enter');
    setError('');
    setStatusMessage('');
  }

  async function handleValidate(event) {
    event.preventDefault();
    if (validating || redeeming || !code.trim()) {
      return;
    }
    if (status === 'anonymous') {
      navigate(`${paths.auth}?returnTo=${encodeURIComponent(authReturnTo)}`);
      return;
    }
    setValidating(true);
    setError('');
    setStatusMessage(t('assessment.companyCode.validating'));
    try {
      const response = await companyApi.inspectAccessCode({ code: normalizeAccessCode(code) });
      const mapped = mapAccessCodeInspectPreview(response);
      if (!mapped.companyName) {
        setError(t('assessment.companyCode.unavailable'));
        return;
      }
      setPreview(mapped);
      setConsent(false);
      setStep('consent');
      setStatusMessage(t('assessment.companyCode.validated', { company: mapped.companyName }));
    } catch (err) {
      if (err instanceof ApiError && err.isUnauthorized()) {
        navigate(`${paths.auth}?returnTo=${encodeURIComponent(authReturnTo)}`);
        return;
      }
      setError(mapAccessCodeError(err, t));
      setStatusMessage('');
    } finally {
      setValidating(false);
    }
  }

  async function handleRedeem() {
    if (validating || redeeming || !consent || !code.trim()) {
      return;
    }
    setRedeeming(true);
    setError('');
    setStatusMessage(t('assessment.companyCode.redeeming'));
    const normalizedCode = normalizeAccessCode(code);
    try {
      const response = await companyApi.redeemAccessCode({
        code: normalizedCode,
        share_result_with_company: true,
      });
      const attemptId = resolveRedeemAttemptId(response);
      if (!attemptId) {
        setError(t('assessment.companyCode.unavailable'));
        setStatusMessage('');
        return;
      }
      await invalidateAfterAssessmentStart(queryClient, attemptId);
      resetFlow();
      navigate(paths.assessmentAttempt(attemptId), { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.isUnauthorized()) {
        navigate(`${paths.auth}?returnTo=${encodeURIComponent(authReturnTo)}`);
        return;
      }
      setError(mapAccessCodeError(err, t));
      setStatusMessage('');
    } finally {
      setRedeeming(false);
    }
  }

  const consentText = locale === 'ar'
    ? (preview?.consent?.text_ar || t('assessment.companyCode.consent'))
    : (preview?.consent?.text_en || t('assessment.companyCode.consent'));
  const redeemLabel = preview?.nextAction === 'resume'
    ? t('assessment.companyCode.resumeAttempt')
    : t('assessment.companyCode.startAttempt');
  const actionHint = preview?.nextAction === 'resume'
    ? t('assessment.companyCode.resumeHint')
    : t('assessment.companyCode.startHint');
  const submitting = validating || redeeming;

  return (
    <Card className="intro-path intro-path-company" data-testid="company-code-section">
      <h2>{t('assessment.companyCode.title')}</h2>
      <p className="muted">{t('assessment.companyCode.description')}</p>
      <LiveRegion message={statusMessage} />
      {step === 'enter' ? (
        <form onSubmit={(event) => { void handleValidate(event); }}>
          <TextInput
            id={codeId}
            label={t('assessment.companyCode.codeLabel')}
            hint={t('company.codeEntry.codeHint')}
            value={code}
            onChange={(event) => setCode(event.target.value.toUpperCase())}
            autoComplete="off"
            spellCheck={false}
            ltr
            required
            disabled={submitting}
          />
          {error ? <p className="field-error" role="alert">{error}</p> : null}
          <div className="action-row">
            <Button type="submit" loading={validating} disabled={redeeming || !code.trim()}>
              {t('assessment.companyCode.validate')}
            </Button>
          </div>
        </form>
      ) : (
        <div
          ref={previewRef}
          tabIndex={-1}
          className="company-code-preview"
          data-testid="company-code-preview"
        >
          <StatusChip tone="accent">
            {t('assessment.companyCode.companyLabel', { companyName: preview.companyName })}
          </StatusChip>
          <p>{t('assessment.companyCode.sponsorExplain')}</p>
          <p className="muted">{actionHint}</p>
          {preview.expiresAt ? (
            <p className="muted">{t('assessment.companyCode.expiresAt', { date: formatDateTime(preview.expiresAt, locale) })}</p>
          ) : null}
          <label className="checkbox-field" htmlFor={consentId}>
            <input
              id={consentId}
              type="checkbox"
              checked={consent}
              onChange={(event) => setConsent(event.target.checked)}
              disabled={submitting}
            />
            <span>{consentText}</span>
          </label>
          {error ? <p className="field-error" role="alert">{error}</p> : null}
          <div className="action-row">
            <Button
              disabled={!consent}
              loading={redeeming}
              onClick={() => { void handleRedeem(); }}
            >
              {redeemLabel}
            </Button>
            <button
              type="button"
              className="btn btn-ghost"
              disabled={submitting}
              onClick={resetFlow}
            >
              {t('assessment.companyCode.cancel')}
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}